import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi, UserManagement as UserManagementType } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, KeyRound, Trash2, Save, UserCog } from 'lucide-react';
import '../styles/PageLayout.css';

const UserManagement: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const [users, setUsers] = useState<UserManagementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<{ [userId: string]: string[] }>({});
  const [showCreateGoogleUser, setShowCreateGoogleUser] = useState(false);
  const [newGoogleUserEmail, setNewGoogleUserEmail] = useState('');
  const [newGoogleUserDisplayName, setNewGoogleUserDisplayName] = useState('');
  const [creatingGoogleUser, setCreatingGoogleUser] = useState(false);

  // Native user creation states
  const [showCreateNativeUser, setShowCreateNativeUser] = useState(false);
  const [newNativeUserEmail, setNewNativeUserEmail] = useState('');
  const [newNativeUserPassword, setNewNativeUserPassword] = useState('');
  const [newNativeUserDisplayName, setNewNativeUserDisplayName] = useState('');
  const [creatingNativeUser, setCreatingNativeUser] = useState(false);

  // Password reset states
  const [showResetPassword, setShowResetPassword] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Delete user states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Force rebuild timestamp: 2025-09-18 22:40

  // Check if current user is admin
  const isAdmin = user?.roles?.includes('Admin') || false;

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied. Admin role required.');
      setLoading(false);
      return;
    }

    loadUsers();
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await authApi.getAllUsers();
      setUsers(usersData);
      
      // Initialize userRoles state with current roles
      const rolesState: { [userId: string]: string[] } = {};
      usersData.forEach(user => {
        rolesState[user.id] = [...user.roles];
      });
      setUserRoles(rolesState);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (userId: string, role: string) => {
    setUserRoles(prev => {
      const currentRoles = prev[userId] || [];
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter(r => r !== role)
        : [...currentRoles, role];
      
      return {
        ...prev,
        [userId]: newRoles
      };
    });
  };

  const handleSaveRoles = async (userId: string) => {
    try {
      setSavingUserId(userId);
      const roles = userRoles[userId] || [];
      
      await authApi.updateUserRoles(userId, roles);
      
      // Update the users list with new roles
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, roles: [...roles] }
          : user
      ));
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user roles');
    } finally {
      setSavingUserId(null);
    }
  };

  const hasRoleChanges = (userId: string) => {
    const currentRoles = users.find(u => u.id === userId)?.roles || [];
    const newRoles = userRoles[userId] || [];

    if (currentRoles.length !== newRoles.length) return true;
    return !currentRoles.every(role => newRoles.includes(role));
  };

  const handleCreateGoogleUser = async () => {
    if (!newGoogleUserEmail.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setCreatingGoogleUser(true);
      setError(null);

      await authApi.createGoogleUser(
        newGoogleUserEmail.trim(),
        newGoogleUserDisplayName.trim() || undefined
      );

      // Reload users list
      await loadUsers();

      // Reset form
      setNewGoogleUserEmail('');
      setNewGoogleUserDisplayName('');
      setShowCreateGoogleUser(false);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Google user');
    } finally {
      setCreatingGoogleUser(false);
    }
  };

  const handleCreateNativeUser = async () => {
    if (!newNativeUserEmail.trim()) {
      setError('Email is required');
      return;
    }

    if (!newNativeUserPassword.trim() || newNativeUserPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setCreatingNativeUser(true);
      setError(null);

      await authApi.createNativeUser(
        newNativeUserEmail.trim(),
        newNativeUserPassword.trim(),
        newNativeUserDisplayName.trim() || undefined
      );

      // Reload users list
      await loadUsers();

      // Reset form
      setNewNativeUserEmail('');
      setNewNativeUserPassword('');
      setNewNativeUserDisplayName('');
      setShowCreateNativeUser(false);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create native user');
    } finally {
      setCreatingNativeUser(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!newPassword.trim() || newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setResettingPassword(true);
      setError(null);

      await authApi.resetUserPassword(userId, newPassword.trim());

      // Reset form
      setNewPassword('');
      setShowResetPassword(null);

      // Show success message (you could add a success state if needed)
      alert('Password reset successfully!');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setDeletingUserId(userId);
      setError(null);

      await authApi.deleteUser(userId);

      // Reload users list
      await loadUsers();

      // Reset state
      setShowDeleteConfirm(null);

      // Show success message
      alert('User deleted successfully!');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <div className="page-container"><div className="page-loading"><p>Access Denied – You need Admin privileges.</p></div></div>
    );
  }

  if (loading) {
    return (
      <div className="page-container"><div className="page-loading"><div className="page-spinner" /><p>Loading users...</p></div></div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setShowCreateGoogleUser(true)}>
            <Plus size={14} /> Google User
          </button>
          <button className="page-btn page-btn-create" onClick={() => setShowCreateNativeUser(true)}>
            <Plus size={14} /> Native User
          </button>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{users.length} Benutzer</span>
        </div>
      </div>

      {error && (
        <div className="page-error-banner">{error}</div>
      )}

      {/* Create Google User Modal */}
      {showCreateGoogleUser && (
        <div className="page-modal-overlay">
          <div className="page-modal-content">
            <h3>Create Google User</h3>
            <div className="page-form-field">
              <label>Email *</label>
              <input type="email" value={newGoogleUserEmail} onChange={(e) => setNewGoogleUserEmail(e.target.value)} placeholder="user@gmail.com" />
            </div>
            <div className="page-form-field">
              <label>Display Name</label>
              <input type="text" value={newGoogleUserDisplayName} onChange={(e) => setNewGoogleUserDisplayName(e.target.value)} placeholder="John Doe" />
            </div>
            <div className="page-modal-actions">
              <button className="page-toggle-btn" onClick={() => { setShowCreateGoogleUser(false); setNewGoogleUserEmail(''); setNewGoogleUserDisplayName(''); setError(null); }} disabled={creatingGoogleUser}>Cancel</button>
              <button className="page-toggle-btn page-btn-create" onClick={handleCreateGoogleUser} disabled={creatingGoogleUser || !newGoogleUserEmail.trim()}>
                {creatingGoogleUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Native User Modal */}
      {showCreateNativeUser && (
        <div className="page-modal-overlay">
          <div className="page-modal-content">
            <h3>Create Native User</h3>
            <div className="page-form-field">
              <label>Email *</label>
              <input type="email" value={newNativeUserEmail} onChange={(e) => setNewNativeUserEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <div className="page-form-field">
              <label>Password *</label>
              <input type="password" value={newNativeUserPassword} onChange={(e) => setNewNativeUserPassword(e.target.value)} placeholder="Minimum 6 characters" />
            </div>
            <div className="page-form-field">
              <label>Display Name</label>
              <input type="text" value={newNativeUserDisplayName} onChange={(e) => setNewNativeUserDisplayName(e.target.value)} placeholder="Optional display name" />
            </div>
            <div className="page-modal-actions">
              <button className="page-toggle-btn" onClick={() => { setShowCreateNativeUser(false); setNewNativeUserEmail(''); setNewNativeUserPassword(''); setNewNativeUserDisplayName(''); setError(null); }} disabled={creatingNativeUser}>Cancel</button>
              <button className="page-toggle-btn page-btn-create" onClick={handleCreateNativeUser} disabled={creatingNativeUser || !newNativeUserEmail.trim() || !newNativeUserPassword.trim()}>
                {creatingNativeUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      <table className="page-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Display Name</th>
            <th>Account Type</th>
            <th>Created</th>
            <th className="text-center">Roles</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((userData) => (
            <tr key={userData.id}>
              <td><strong>{userData.email}</strong></td>
              <td>{userData.displayName || '-'}</td>
              <td>
                <span className="page-badge" style={{
                  backgroundColor: userData.isGoogleAccount ? '#e3f2fd' : userData.isMicrosoftAccount ? '#e8f4fd' : '#f3e5f5',
                  color: userData.isGoogleAccount ? '#1976d2' : userData.isMicrosoftAccount ? '#0078d4' : '#7b1fa2'
                }}>
                  {userData.isGoogleAccount ? 'Google' : userData.isMicrosoftAccount ? 'Microsoft' : 'Local'}
                </span>
              </td>
              <td className="page-table-meta">{formatDate(userData.createdUtc)}</td>
              <td className="text-center">
                <div className="page-action-group">
                  {['Admin', 'User'].map(role => (
                    <label key={role} className="page-checkbox-label" style={{
                      backgroundColor: (userRoles[userData.id] || []).includes(role) ? '#e8f5e8' : '#f8f9fa'
                    }}>
                      <input type="checkbox" checked={(userRoles[userData.id] || []).includes(role)} onChange={() => handleRoleToggle(userData.id, role)} />
                      <span>{role}</span>
                    </label>
                  ))}
                </div>
              </td>
              <td className="text-center">
                <div className="page-action-group">
                  <button className="page-toggle-btn page-btn-create" onClick={() => handleSaveRoles(userData.id)} disabled={!hasRoleChanges(userData.id) || savingUserId === userData.id} style={{ opacity: savingUserId === userData.id ? 0.7 : 1 }}>
                    <Save size={14} /> {savingUserId === userData.id ? 'Saving...' : 'Save'}
                  </button>
                  {!userData.isGoogleAccount && !userData.isMicrosoftAccount && (
                    <button className="page-toggle-btn" onClick={() => setShowResetPassword(userData.id)} style={{ backgroundColor: '#ffc107', color: '#212529' }}>
                      <KeyRound size={14} /> Reset
                    </button>
                  )}
                  <button className="page-toggle-btn page-btn-delete" onClick={() => setShowDeleteConfirm(userData.id)} disabled={deletingUserId === userData.id} style={{ opacity: deletingUserId === userData.id ? 0.7 : 1 }}>
                    <Trash2 size={14} /> {deletingUserId === userData.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {users.length === 0 && (
        <div className="page-empty-state"><p>No users found.</p></div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="page-modal-overlay">
          <div className="page-modal-content">
            <h3>Reset Password</h3>
            <p className="page-table-meta" style={{ marginBottom: 16 }}>
              Reset password for: <strong>{users.find(u => u.id === showResetPassword)?.email}</strong>
            </p>
            <div className="page-form-field">
              <label>New Password *</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimum 6 characters" />
            </div>
            <div className="page-modal-actions">
              <button className="page-toggle-btn" onClick={() => { setShowResetPassword(null); setNewPassword(''); setError(null); }} disabled={resettingPassword}>Cancel</button>
              <button className="page-toggle-btn page-btn-delete" onClick={() => handleResetPassword(showResetPassword)} disabled={resettingPassword || !newPassword.trim() || newPassword.length < 6}>
                {resettingPassword ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="page-modal-overlay">
          <div className="page-modal-content">
            <h3 style={{ color: '#dc3545' }}>Delete User</h3>
            <p className="page-table-meta" style={{ marginBottom: 16 }}>
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="page-detail-box">
              <strong>User:</strong> {users.find(u => u.id === showDeleteConfirm)?.email}<br/>
              <strong>Display Name:</strong> {users.find(u => u.id === showDeleteConfirm)?.displayName || 'N/A'}<br/>
              <strong>Account Type:</strong> {users.find(u => u.id === showDeleteConfirm)?.isGoogleAccount ? 'Google' : users.find(u => u.id === showDeleteConfirm)?.isMicrosoftAccount ? 'Microsoft' : 'Local'}
            </div>
            <div className="page-modal-actions">
              <button className="page-toggle-btn" onClick={() => { setShowDeleteConfirm(null); setError(null); }} disabled={deletingUserId === showDeleteConfirm}>Cancel</button>
              <button className="page-toggle-btn page-btn-delete" onClick={() => handleDeleteUser(showDeleteConfirm)} disabled={deletingUserId === showDeleteConfirm}>
                {deletingUserId === showDeleteConfirm ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
